import React, { useEffect, useState } from "react";
import axios from "axios";
import { logout } from "../apiAuth";

const ActiveDevices = () => {
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("Loading security data...");

  const loadData = async () => {
    setStatus("Loading security data...");

    try {
      const devicesResponse = await axios.get("/petgrooming/auth/devices/");
      const historyResponse = await axios.get("/petgrooming/auth/login-history/");

      setDevices(devicesResponse.data.devices || []);
      setEvents(historyResponse.data.events || []);
      setStatus("");
    } catch (error) {
      console.error("ActiveDevices error:", error.response?.data || error);
      setStatus(
        error.response?.data?.detail ||
        error.message ||
        "Could not load active devices."
      );
    }
  };

  const revokeDevice = async (device) => {
    const ok = window.confirm(
      device.is_current
        ? "This is your current device. You will be logged out. Continue?"
        : "Logout this device?"
    );

    if (!ok) return;

    try {
      await axios.post(`/petgrooming/auth/devices/${device.device_id}/revoke/`);

      if (device.is_current) {
        logout();
        return;
      }

      await loadData();
    } catch (error) {
      console.error(error.response?.data || error);
      setStatus(error.response?.data?.detail || "Could not revoke device.");
    }
  };

  const revokeOthers = async () => {
    const ok = window.confirm("Logout all other devices?");
    if (!ok) return;

    try {
      await axios.post("/petgrooming/auth/devices/revoke-others/");
      await loadData();
    } catch (error) {
      console.error(error.response?.data || error);
      setStatus(error.response?.data?.detail || "Could not revoke other devices.");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Active Devices</h1>
          <p style={styles.subtitle}>
            Logged-in devices and login history for Arys Dashboard.
          </p>
        </div>

        <button onClick={revokeOthers} style={styles.dangerButton}>
          Logout other devices
        </button>
      </div>

      {status && (
        <div style={styles.notice}>
          {status}
        </div>
      )}

      <h2 style={styles.sectionTitle}>Devices</h2>

      {devices.length === 0 && !status && (
        <p style={styles.notice}>No devices found.</p>
      )}

      <div style={styles.grid}>
        {devices.map((device) => (
          <div
            key={device.device_id}
            style={{
              ...styles.card,
              borderColor: device.is_current ? "#b8794a" : "#eee",
            }}
          >
            <h3 style={styles.cardTitle}>
              {device.device_name || "Unknown device"}
            </h3>

            {device.is_current && (
              <p style={styles.current}>Current device</p>
            )}

            <p><strong>IP:</strong> {device.ip_address || "Unknown"}</p>
            <p>
              <strong>Last seen:</strong>{" "}
              {device.last_seen ? new Date(device.last_seen).toLocaleString() : "Unknown"}
            </p>
            <p>
              <strong>Expires:</strong>{" "}
              {device.expires_at ? new Date(device.expires_at).toLocaleString() : "Unknown"}
            </p>

            <p style={styles.ua}>{device.user_agent || "No user agent"}</p>

            {device.is_active ? (
              <button
                onClick={() => revokeDevice(device)}
                style={styles.revokeButton}
              >
                {device.is_current ? "Logout this device" : "Logout device"}
              </button>
            ) : (
              <p style={styles.revoked}>Revoked / expired</p>
            )}
          </div>
        ))}
      </div>

      <h2 style={styles.sectionTitle}>Login History</h2>

      <div style={styles.tableBox}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Reason</th>
              <th style={styles.th}>IP</th>
            </tr>
          </thead>

          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td style={styles.td}>
                  {event.created_at ? new Date(event.created_at).toLocaleString() : ""}
                </td>
                <td style={styles.td}>{event.status}</td>
                <td style={styles.td}>{event.reason}</td>
                <td style={styles.td}>{event.ip_address || ""}</td>
              </tr>
            ))}

            {events.length === 0 && (
              <tr>
                <td style={styles.td} colSpan="4">No login history found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "28px",
    minHeight: "100vh",
    background: "#f6eee8",
    color: "#4b2e20",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
  },
  subtitle: {
    marginTop: "6px",
    color: "#7c6255",
  },
  sectionTitle: {
    marginTop: "24px",
  },
  notice: {
    background: "#fff",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "16px",
    border: "1px solid #eadbd1",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
  },
  card: {
    background: "#fff",
    border: "2px solid #eee",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  },
  cardTitle: {
    margin: "0 0 10px",
  },
  current: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#fff6e8",
    color: "#8a4b20",
    fontWeight: 700,
  },
  ua: {
    fontSize: "12px",
    wordBreak: "break-word",
    background: "#faf7f4",
    borderRadius: "10px",
    padding: "10px",
    color: "#7c6255",
  },
  revokeButton: {
    width: "100%",
    padding: "12px",
    border: 0,
    borderRadius: "14px",
    background: "#b8794a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  dangerButton: {
    padding: "12px 16px",
    border: 0,
    borderRadius: "14px",
    background: "#7f1d1d",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  revoked: {
    color: "#9ca3af",
    fontWeight: 700,
  },
  tableBox: {
    background: "#fff",
    borderRadius: "18px",
    padding: "12px",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "10px",
    borderBottom: "1px solid #eee",
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #f2f2f2",
  },
};

export default ActiveDevices;
